import React, { useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../ui/card';
import { Button } from '../../../ui/button';
import { Badge } from '../../../ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../ui/table';
import { Input } from '../../../ui/input';
import { Alert, AlertDescription, AlertTitle } from '../../../ui/alert';
import { INVITATIONS, LOCATIONS, statusVariant, prettyDate } from '../../fixtures';
import { Upload, UserPlus, Mail, Search, AlertTriangle, Download, Trash2 } from 'lucide-react';

const statusLabel = (s) =>
  ({
    completed: 'Completed',
    started: 'In progress',
    sent: 'Not started',
    pending: 'Not sent',
  }[s] || s);

const EmployeesTab = ({ campaign, onImportClick }) => {
  const [query, setQuery] = useState('');
  const [fteEdits, setFteEdits] = useState(() =>
    LOCATIONS.reduce((acc, l) => ({ ...acc, [l.id]: l.fte }), {}),
  );

  const filtered = useMemo(() => {
    if (!query) return INVITATIONS;
    const q = query.toLowerCase();
    return INVITATIONS.filter(
      (i) =>
        i.name.toLowerCase().includes(q) ||
        i.email.toLowerCase().includes(q) ||
        i.location.toLowerCase().includes(q),
    );
  }, [query]);

  const isDraft = campaign.status === 'draft';

  return (
    <div className='grid grid-cols-1 xl:grid-cols-[320px_1fr] gap-6'>
      <div className='space-y-4'>
        <Card>
          <CardHeader>
            <CardTitle className='text-sm flex items-center justify-between'>
              Office locations &amp; FTE
              <Badge variant='outline' size='sm'>
                {LOCATIONS.length}
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className='space-y-3'>
            <p className='text-xs text-muted-foreground'>
              FTE counts are used to extrapolate respondent totals to your full workforce (PRD §5c.5).
            </p>
            {LOCATIONS.map((location) => (
              <div key={location.id} className='grid grid-cols-[1fr_88px] gap-2 items-center'>
                <div className='text-sm'>{location.name}</div>
                <Input
                  type='number'
                  value={fteEdits[location.id]}
                  onChange={(e) =>
                    setFteEdits((prev) => ({ ...prev, [location.id]: Number(e.target.value || 0) }))
                  }
                  className='h-8 text-right'
                />
              </div>
            ))}
            <Button variant='outline' size='sm' className='w-full'>
              <UserPlus size={14} strokeWidth={1.5} />
              Add location
            </Button>
          </CardContent>
        </Card>

        {isDraft && (
          <Alert>
            <AlertTriangle size={14} strokeWidth={1.5} />
            <AlertTitle>Setup required</AlertTitle>
            <AlertDescription>
              Import your employee list below, then we can send invitations. Each employee gets a unique link.
            </AlertDescription>
          </Alert>
        )}
      </div>

      <Card>
        <CardHeader className='flex-row items-center justify-between space-y-0'>
          <div>
            <CardTitle className='text-base'>Employee invitations</CardTitle>
            <p className='text-xs text-muted-foreground mt-1'>
              {campaign.invitedCount > 0
                ? `${campaign.invitedCount} invited · ${campaign.completedCount} completed · ${campaign.invitedCount - campaign.completedCount} outstanding`
                : 'No employees added yet'}
            </p>
          </div>
          <div className='flex items-center gap-2'>
            <Button variant='outline' size='sm'>
              <Download size={14} strokeWidth={1.5} />
              Template CSV
            </Button>
            <Button size='sm' onClick={onImportClick}>
              <Upload size={14} strokeWidth={1.5} />
              Import employees
            </Button>
          </div>
        </CardHeader>

        <div className='flex items-center gap-3 px-5 py-3 border-b border-border'>
          <div className='relative flex-1 max-w-sm'>
            <Search
              size={14}
              strokeWidth={1.5}
              className='absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground'
            />
            <Input
              placeholder='Search name or email...'
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className='pl-9 h-8 text-sm'
            />
          </div>
          <Badge variant='outline' size='sm'>
            {filtered.length} shown
          </Badge>
        </div>

        {campaign.invitedCount === 0 ? (
          <CardContent className='py-16 text-center'>
            <div className='mx-auto w-12 h-12 rounded-full bg-muted flex items-center justify-center mb-4'>
              <Upload size={18} strokeWidth={1.5} className='text-muted-foreground' />
            </div>
            <h4 className='font-semibold mb-1'>No employees yet</h4>
            <p className='text-sm text-muted-foreground mb-5 max-w-sm mx-auto'>
              Import via CSV (name, email, location) or add entries one by one.
            </p>
            <div className='flex items-center justify-center gap-2'>
              <Button variant='outline' onClick={onImportClick}>
                <Upload size={14} strokeWidth={1.5} />
                Import CSV
              </Button>
              <Button variant='outline'>
                <UserPlus size={14} strokeWidth={1.5} />
                Add manually
              </Button>
            </div>
          </CardContent>
        ) : (
          <Table wrapperClassName='rounded-none border-0 shadow-none'>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Location</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Sent</TableHead>
                <TableHead>Completed</TableHead>
                <TableHead className='text-right'>Reminders</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((inv) => (
                <TableRow key={inv.id}>
                  <TableCell>
                    <div className='font-medium flex items-center gap-2'>
                      {inv.name}
                      {inv.flagged && (
                        <Badge variant='warning' size='sm'>
                          Flagged
                        </Badge>
                      )}
                    </div>
                    <div className='text-xs text-muted-foreground'>{inv.email}</div>
                  </TableCell>
                  <TableCell className='text-sm'>{inv.location}</TableCell>
                  <TableCell>
                    <Badge variant={statusVariant(inv.status)} size='default'>
                      {statusLabel(inv.status)}
                    </Badge>
                  </TableCell>
                  <TableCell className='text-xs text-muted-foreground'>{prettyDate(inv.sentAt)}</TableCell>
                  <TableCell className='text-xs text-muted-foreground'>{prettyDate(inv.completedAt)}</TableCell>
                  <TableCell className='text-right tabular-nums text-xs'>{inv.reminderCount}</TableCell>
                  <TableCell className='text-right'>
                    <div className='flex items-center gap-1 justify-end'>
                      {inv.status !== 'completed' && (
                        <Button variant='ghost' size='icon' title='Resend invitation'>
                          <Mail size={14} strokeWidth={1.5} />
                        </Button>
                      )}
                      <Button variant='ghost' size='icon' title='Remove'>
                        <Trash2 size={14} strokeWidth={1.5} className='text-destructive/70' />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>
    </div>
  );
};

export default EmployeesTab;
